import { useState, type FocusEvent, type MouseEvent, type ReactElement } from 'react';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { ButtonProps } from '@components/button';
import { IconName, Icon } from '@components/icon';
import './FloatingButton.scss';

type excludedButtonProps =
  | 'endIcon'
  | 'startIcon'
  | 'endIconSize'
  | 'startIconSize'
  | 'size'
  | 'colorScheme'
  | 'variant'
  | 'disabled'
  | 'children'
  | 'aria-label';

export type FloatingButtonIcon = IconName | ReactElement;

type ButtonAsButtonProps = Extract<ButtonProps, { href?: undefined }>;

export interface FloatingButtonProps
  extends Partial<Omit<ButtonAsButtonProps, excludedButtonProps>> {
  /** Design variant. Secondary has pill style with label on left */
  variant?: 'primary' | 'secondary';
  /** Icon to display by default */
  iconDefault: FloatingButtonIcon;
  /** Icon to display on hover when button is active */
  iconHover?: FloatingButtonIcon;
  /** Icon to display when active */
  iconActive?: FloatingButtonIcon;
  /** Whether the button is showing the active state */
  isActive?: boolean;
  /** Has animation when changing icons. Default false */
  hasAnimation?: boolean;
  /** Label to display */
  label?: string;
  /** Label to display when isActive is true */
  labelActive?: string;
  /** Size of the button (small or large). Default large */
  size?: 'small' | 'large';
  /** Aria label for the button. Please use when no label is provided */
  ariaLabel?: string;
  /** Aria label for the button when active */
  ariaLabelActive?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (e: MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (e: MouseEvent<HTMLButtonElement>) => void;
}

const renderIcon = (icon: FloatingButtonIcon) =>
  typeof icon === 'string' ? <Icon name={icon} /> : icon;

/**
 * FloatingButton Component. Shows an icon with an optional active state.
 * If iconHover is provided, it will only show on hover when isActive is true.
 * Use aria-labels for accessibility when no label is provided.
 * @param variant - Design variant. Secondary has pill style with label on left
 * @param iconDefault - Icon to display by default. An IconName, or an element for custom artwork
 * @param iconHover - Icon to display on hover when button is active
 * @param iconActive - Icon to display when active
 * @param isActive - Whether the button is showing the active state
 * @param hasAnimation - Has animation when changing icons. Default false
 * @param label - Label to display by default
 * @param labelActive - Label to display when isActive is true
 * @param size - Size of the button (small or large). Default large
 * @param ariaLabel - Aria label for the button
 * @param ariaLabelActive - Aria label for the button when active
 * @param className - Additional classes to add
 * @param onClick - Function to call on click
 * @param onMouseEnter - Function to call on mouse enter
 * @param onMouseLeave - Function to call on mouse leave
 */
export const FloatingButton: React.FC<FloatingButtonProps> = ({
  variant = 'primary',
  iconDefault,
  iconHover,
  iconActive,
  isActive = false,
  hasAnimation = false,
  label,
  labelActive,
  size = 'large',
  type = 'button',
  ariaLabelActive: ariaLabelActiveProp,
  ariaLabel: ariaLabelProp,
  className,
  onClick,
  onMouseEnter,
  onMouseLeave,
  ...rest
}) => {
  const [isHover, setIsHover] = useState(false);
  const AnimationWrapper = motion.div;
  const isSecondary = variant === 'secondary';

  // secondary variant needs an aria-label since the label is separate and not always visible
  const ariaLabel = ariaLabelProp || (isSecondary ? label : '');
  const ariaLabelActive = ariaLabelActiveProp || (isSecondary ? labelActive : '');

  const getCurrentIcon = (): FloatingButtonIcon => {
    if (isHover && iconHover) {
      if (isActive && iconActive) return iconHover;
      if (!iconActive) return iconHover;
    }

    if (isActive && iconActive) return iconActive;

    return iconDefault;
  };

  const currentIcon = getCurrentIcon();

  // An icon name keys the animation on its own; an element keys on the slot it came from.
  const getIconKey = (icon: FloatingButtonIcon) => {
    if (typeof icon === 'string') return icon;
    if (icon === iconActive) return 'active';
    if (icon === iconHover) return 'hover';

    return 'default';
  };

  const handleMouseEnter = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsHover(true);
    if (onMouseEnter) onMouseEnter(e);
  };

  const handleMouseLeave = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsHover(false);
    if (onMouseLeave) onMouseLeave(e);
  };

  const handleFocus = (e: FocusEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsHover(true);
  };

  const handleBlur = (e: FocusEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsHover(false);
  };

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (type === 'submit') e.preventDefault();
    if (onClick) onClick(e);
  };

  return (
    <div
      className={classNames(
        'vds-floating-button',
        isSecondary && isHover && 'vds-floating-button--pill',
        className,
      )}>
      {isSecondary && (
        <div
          className={classNames(
            'vds-floating-button__left-label',
            isHover && 'vds-floating-button__left-label--hover',
            { [`vds-button-text-${size === 'small' ? 'medium' : size}`]: size },
          )}>
          {isActive ? labelActive : label}
        </div>
      )}
      <button
        className={classNames(
          'vds-floating-button__button',
          isActive && 'vds-floating-button--active',
          {
            [`vds-floating-button--${size}`]: size,
            [`vds-button-text-${size === 'small' ? 'medium' : size}`]: size,
          },
        )}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-label={isActive ? ariaLabelActive : ariaLabel}
        type={type}
        {...rest}>
        {currentIcon && (
          <div className="vds-floating-button__icon-wrapper">
            {hasAnimation ? (
              <AnimationWrapper
                className="vds-floating-button__icon-animation"
                key={getIconKey(currentIcon)}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}>
                {renderIcon(currentIcon)}
              </AnimationWrapper>
            ) : (
              renderIcon(currentIcon)
            )}
          </div>
        )}
        {!isSecondary && (isActive ? labelActive : label)}
      </button>
    </div>
  );
};
